import React from 'react';
import SummaryCard from 'src/shared/components/card-templates/SummaryCard';
import { Loader } from 'src/shared/components/loader/Loader';
import CatalogueLayerCredit from 'src/shared/catalogue/CatalogueLayerCredit';
import { PROTECTED_AREAS_CATALOGUE_LAYER } from 'src/shared/catalogue/protected-areas/protectedAreas';
import ProtectedAreasCoverageNotice from './ProtectedAreasCoverageNotice';
import { useGetSiteProtectedAreasQuery } from '../../state/api/siteNatureRiskEngineSlice';
import type { OverlapType } from '../../state/api/siteProtectedAreasQuery';

type ProtectedAreasSummaryWidgetProps = {
    organisationBusinessCentralId: string;
    siteId: string;
    /** Selected time period — the count reflects the overlaps recorded then. */
    at?: string;
};

// This card only reads the count, so it asks for the smallest page.
const COUNT_ONLY = 1;

// Everything the widget reports: areas overlapping the site, and those adjacent
// to it. The engine returns nothing else, so this is the whole list.
const REPORTED: OverlapType[] = ['OVERLAPPING', 'ADJACENT'];

// Attribution for the protected-areas dataset. CMS-driven; this slug is a
// placeholder until the entry exists.

const ProtectedAreasSummaryWidget = ({ organisationBusinessCentralId, siteId, at }: ProtectedAreasSummaryWidgetProps) => {
    const { data, isLoading, isError } = useGetSiteProtectedAreasQuery(
        { organisationBusinessCentralId, siteId, at, page: 1, pageSize: COUNT_ONLY, overlapTypes: REPORTED },
        { skip: !organisationBusinessCentralId || !siteId }
    );

    if (isLoading) {
        return (
            <SummaryCard
                cardIcon={null}
                cardTitle='Protected areas'
                containerClasses='gap-4 p-5'
                cardBody={
                    <div className='flex min-h-[120px] items-center justify-center'>
                        <Loader />
                    </div>
                }
            />
        );
    }

    // A failed read is not an empty dataset. The engine draws that distinction
    // deliberately — it errors rather than answering zero when it cannot read the
    // artifact — so saying nothing here would throw the distinction away and let a
    // broken read look like a site with no protected areas.
    if (isError) {
        return (
            <SummaryCard
                cardIcon={null}
                cardTitle='Protected areas'
                containerClasses='gap-4 p-5'
                cardBody={
                    <p data-testid='protected-areas-summary-error' className='text-sm text-grey-80'>
                        Protected areas could not be loaded. Try again shortly.
                    </p>
                }
            />
        );
    }

    const site = data?.site;

    // No site means no access, or no such site. Neither a count nor a coverage gap can
    // be claimed from that, so the card stands down rather than guessing at either.
    if (!site) {
        return null;
    }

    const total = site.protectedAreas.pagination.totalItems;
    // The engine reports coverage, which is what makes a zero readable: without it a
    // zero means "not surveyed yet", and any areas alongside it were picked up across
    // a border.
    const hasProtectedAreasCoverage = Boolean(site.hasProtectedAreasCoverage);
    // The count is withheld when it would be a bare zero we cannot stand behind.
    const showsCount = total > 0 || hasProtectedAreasCoverage;

    return (
        <SummaryCard
            cardIcon={null}
            cardTitle={
                <span className='flex items-center gap-2'>
                    Protected areas
                    <CatalogueLayerCredit layer={PROTECTED_AREAS_CATALOGUE_LAYER} placement='right' />
                </span>
            }
            containerClasses='gap-4 p-5'
            cardBody={
                <div data-testid='protected-areas-summary-widget' className='flex flex-col gap-4'>
                    {showsCount && (
                        <>
                            <div className='flex items-baseline gap-2'>
                                <span data-testid='protected-areas-summary-count' className='text-4xl font-semibold'>
                                    {total}
                                </span>
                                <span className='text-2xl text-grey-80'>overlapping or adjacent</span>
                            </div>

                            <p className='text-sm text-grey-80'>
                                Legally protected and designated areas overlapping or adjacent to this site. Operators are commonly asked to
                                disclose these when applying for permits and licences.
                            </p>
                        </>
                    )}

                    {!hasProtectedAreasCoverage && <ProtectedAreasCoverageNotice hasAreasNearby={total > 0} />}
                </div>
            }
        />
    );
};

export default ProtectedAreasSummaryWidget;
